<template>
  <div class="history-page">
    <Preloader :backgroundColor="preloaderColor"></Preloader>

    <!-- Header -->
    <HeaderNews
      subtitle="Noticias y contenido sobre Inteligencia Artificial"
      :showSearch="true"
      :searchQuery.sync="query"
      :showMobileMenu="true"
      @toggle-mobile-menu="toggleMobileMenu"
    />

    <!-- Main Layout -->
    <div class="history-layout">
      <!-- Sidebar -->
      <Sidebar :class="{'sidebar--mobile-visible': showMobileSidebar}" @navigate="showMobileSidebar = false" />

      <!-- Mobile Overlay -->
      <div
        v-if="showMobileSidebar"
        class="mobile-overlay"
        @click="toggleMobileMenu"
      ></div>

      <!-- Main Content -->
      <div class="history-body">
        <main class="history-main">
          <div class="history-container">
        <!-- Header con botón limpiar -->
        <div v-if="filteredHistory.length > 0" class="history-header">
          <h2 class="history-header__title">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
            Historial ({{ filteredHistory.length }})
          </h2>
          <button @click="confirmClearHistory" class="clear-history-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="3 6 5 6 21 6"/>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
            </svg>
            Limpiar historial
          </button>
        </div>

        <!-- Empty state -->
        <div v-if="filteredHistory.length === 0 && !loading" class="empty-state">
          <div class="empty-state__icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
          </div>
          <h2 class="empty-state__title">No hay artículos en el historial</h2>
          <p class="empty-state__message">
            Los artículos que leas aparecerán aquí automáticamente
          </p>
          <router-link to="/news" class="empty-state__button">
            Ver todos los artículos
          </router-link>
        </div>

        <!-- Feed List -->
        <FeedList v-else :items="filteredHistory" :loading="loading" />
          </div>
        </main>

        <!-- Footer -->
        <Footer />
      </div>
    </div>
  </div>
</template>

<script>
import HeaderNews from '../components/HeaderNews.vue';
import Sidebar from '../components/Sidebar.vue';
import FeedList from '../components/FeedList.vue';
import Footer from '../components/Footer.vue';
import bookmarksService from '../../../services/bookmarks.js';
import Preloader from '../../../components/Preloader';

export default {
  name: 'History',
  components: {
    HeaderNews,
    Sidebar,
    FeedList,
    Footer,
    Preloader
  },
  data() {
    return {
      history: [],
      query: '',
      loading: false,
      showMobileSidebar: false,
      preloaderColor: 'rgba(14, 18, 23, 0.8)', // Color de fondo del preloader
    };
  },
  computed: {
    filteredHistory() {
      if (!this.query.trim()) {
        return this.history;
      }

      const q = this.query.trim().toLowerCase();
      return this.history.filter(item =>
        (item.title && item.title.toLowerCase().includes(q)) ||
        (item.description && item.description.toLowerCase().includes(q))
      );
    }
  },
  methods: {
    loadHistory() {
      this.history = bookmarksService.getHistory();
    },
    onBookmarkChange() {
      this.loadHistory();
    },
    confirmClearHistory() {
      if (confirm('¿Estás seguro de que deseas limpiar todo el historial?')) {
        bookmarksService.clearHistory();
        this.loadHistory();
      }
    },
    toggleMobileMenu() {
      this.showMobileSidebar = !this.showMobileSidebar;
    }
  },
  mounted() {
    this.loadHistory();
    window.addEventListener('bookmarks-updated', this.onBookmarkChange);
  },
  beforeUnmount() {
    window.removeEventListener('bookmarks-updated', this.onBookmarkChange);
  }
};
</script>

<style scoped>
.history-page {
  min-height: 100vh;
  background: #0e1217;
  color: #e1e7ef;
  display: flex;
  flex-direction: column;
}

/* Main Layout */
.history-layout {
  display: flex;
  flex: 1;
  position: relative;
}

.history-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-left: 240px; /* Ancho del sidebar */
  min-height: calc(100vh - 57px);
  transition: margin-left 0.3s ease;
}

.history-main {
  flex: 1;
  overflow-y: auto;
  padding: 24px 20px 80px; /* Padding bottom para el footer */
}

/* Scrollbar para history-main */
.history-main::-webkit-scrollbar {
  width: 6px;
}
.history-main::-webkit-scrollbar-track {
  background: #1c2127;
  border-radius: 3px;
}
.history-main::-webkit-scrollbar-thumb {
  background: #2d333b;
  border-radius: 3px;
}
.history-main::-webkit-scrollbar-thumb:hover {
  background: #4a9eff;
}

.history-container {
  max-width: 1400px;
  margin: 0 auto;
}

/* Mobile Overlay */
.mobile-overlay {
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 400;
  backdrop-filter: blur(4px);
}

.sidebar--mobile-visible {
  left: 0 !important;
}

/* Header del historial */
.history-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #2d333b;
}

.history-header__title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 20px;
  font-weight: 700;
  color: #e1e7ef;
  margin: 0;
}

.history-header__title svg {
  color: #4a9eff;
}

.clear-history-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: transparent;
  border: 1px solid #2d333b;
  border-radius: 8px;
  color: #ef4444;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: inherit;
}

.clear-history-btn:hover {
  background: rgba(239, 68, 68, 0.1);
  border-color: #ef4444;
}

/* Empty state */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
}

.empty-state__icon {
  width: 80px;
  height: 80px;
  background: rgba(74, 158, 255, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  color: #4a9eff;
}

.empty-state__title {
  font-size: 24px;
  font-weight: 700;
  color: #e1e7ef;
  margin: 0 0 12px 0;
}

.empty-state__message {
  font-size: 16px;
  color: #7d8694;
  margin: 0 0 32px 0;
  max-width: 400px;
  line-height: 1.5;
}

.empty-state__button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #4a9eff, #7b68ee);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(74, 158, 255, 0.3);
  text-decoration: none;
}

.empty-state__button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(74, 158, 255, 0.4);
}

@media (max-width: 768px) {
  .mobile-overlay {
    display: block;
  }

  .history-body {
    margin-left: 0; /* Sin margen en mobile */
  }

  .history-main {
    padding: 16px 12px 60px; /* Ajustar padding bottom */
  }

  .history-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .clear-history-btn {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .history-main {
    padding: 12px 8px;
  }

  .history-header__title {
    font-size: 18px;
  }
}
</style>
